import type { ReadSearchApiArg, SearchScope } from 'lib-search-types';
import {
  SearchFilter,
  type SearchFilterItem,
  type SearchFilterProps,
} from 'lib-search-ui';
import type { PropsWithChildren } from 'lib-shared-types-utils';
import { memo, useCallback, useMemo, useState } from 'react';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';

import { mapSearchResultToFilterItem } from './utils/mapSearchResultToFilterItem';

type SearchScopeFilterProps<TScope extends SearchScope> = Pick<
  SearchFilterProps,
  | 'operators'
  | 'defaultOperator'
  | 'placeholder'
  | 'selectedValues'
  | 'selectedOperator'
  | 'onChange'
  | 'buttonProps'
  | 'popoverContentProps'
> & {
  /** The search scope to target. */
  readonly scope: TScope;
  /**
   * Additional search arguments to include in the search request. The `query`
   * and `page` fields are managed by the component internally.
   */
  readonly initialArg?: Pick<
    ReadSearchApiArg<TScope>,
    'filters' | 'sortBy' | 'pageSize' | 'isStrict'
  >;
  /** Static items to prepend above the search results (e.g. Assigned/Unassigned shortcuts). */
  readonly prependedItems?: readonly SearchFilterItem[];
} & PropsWithChildren;

/**
 * A filter component backed by Search API that fetches and paginates items for a given scope.
 *
 * Wraps the generic `SearchFilter` from lib-search-ui with debounced search, infinite scroll, and
 * scope-aware rendering (e.g. avatars for users).
 */
const SearchScopeFilter = <TScope extends SearchScope>({
  scope,
  initialArg,
  placeholder = 'Search...',
  operators,
  defaultOperator,
  selectedValues,
  selectedOperator,
  onChange,
  prependedItems,
  buttonProps,
  popoverContentProps,
  children,
}: SearchScopeFilterProps<TScope>): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('');

  const [
    readSearch,
    { results, isFetching, isSuccess, hasNextPage, fetchNextPage },
  ] = useLazyReadSearchInfiniteQuery<TScope>();

  const baseArg = useMemo(
    () => ({ scope, ...initialArg }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, JSON.stringify(initialArg)],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        readSearch({ ...baseArg, scope } as ReadSearchApiArg<TScope>);
      }

      if (!open) {
        setSearchTerm('');
      }
    },
    [baseArg, readSearch, scope],
  );

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);

      readSearch(
        {
          ...baseArg,
          scope,
          query: term || undefined,
        } as ReadSearchApiArg<TScope>,
        { debounceTime: 300 },
      );
    },
    [baseArg, readSearch, scope],
  );

  const items = useMemo(
    (): SearchFilterItem[] => [
      ...(prependedItems ?? []),
      ...results.map((result) => ({
        ...mapSearchResultToFilterItem(scope, result),
        highlight: searchTerm,
      })),
    ],
    [prependedItems, results, scope, searchTerm],
  );

  return (
    <SearchFilter
      placeholder={placeholder}
      items={items}
      operators={operators}
      defaultOperator={defaultOperator}
      buttonProps={buttonProps}
      popoverContentProps={popoverContentProps}
      selectedValues={selectedValues}
      selectedOperator={selectedOperator}
      isFetching={isFetching}
      isSuccess={isSuccess}
      hasNextPage={hasNextPage}
      onChange={onChange}
      onOpenChange={handleOpenChange}
      onSearch={handleSearch}
      onLoadMore={fetchNextPage}
    >
      {children}
    </SearchFilter>
  );
};

const _SearchScopeFilter = memo(SearchScopeFilter) as typeof SearchScopeFilter;

export { _SearchScopeFilter as SearchScopeFilter };
